///////////////////////////////////////////////////////////////////////////////////////
//                          Copyright (c) 2020 - 2026
//                                  RXO Inc.
//                         All Rights Reserved Worldwide
//
//WARNING:  This program (or document) is unpublished, proprietary
//property of RXO Inc. and is to be maintained in strict confidence.
//Unauthorized reproduction, distribution or disclosure of this program
//(or document), or any program (or document) derived from it is
//prohibited by State and Federal law, and by local law outside of the U.S.
///////////////////////////////////////////////////////////////////////////////////////

import { cloneDeep, findIndex } from "lodash";
import { mode, division as divisionTypes, bidScacType } from "./types";
import messages from "./messages";

export const handleNewBidValidation = (
  step,
  formData,
  validationData,
  division,
  crmEnableImdlUi = false
) => {
  if (step === 1) {
    return bidInformationValidation(formData, validationData);
  } else if (step === 2) {
    return modeInformationValidation(formData, validationData, division, crmEnableImdlUi);
  } else if (step === 3) {
    return bidStrategyValidation(formData, validationData, crmEnableImdlUi);
  } else if (step === 4) {
    return submitBidValidation(formData, validationData, division, crmEnableImdlUi);
  }
};

export const handleShipperValidation = (formData, validationData) => {
  const { mainSalesRepDetails, shipper } = formData;
  let { bidInformation } = validationData;
  if (!shipper) {
    setValidationDataObj(
      "shipper",
      false,
      messages.thisFieldIsRequired.defaultMessage,
      bidInformation
    );
  } else if (mainSalesRepDetails.noRepErrror) {
    setValidationDataObj(
      "shipper",
      false,
      messages.noMainRepresentativeOnFileForThisShipper.defaultMessage,
      bidInformation
    );
  } else if (mainSalesRepDetails.fetchError) {
    setValidationDataObj(
      "shipper",
      false,
      messages.systemFailedToRetrieveMainRepOnFile.defaultMessage,
      bidInformation
    );
  }
  return validationData;
};

export const handleStrategicQuantileValidation = (
  formData,
  validationData,
  minValue,
  maxValue
) => {
  let { bidStrategy } = validationData;

  const tlModeIndex = findIndex(formData.modes, m => m.mode === mode.tl);
  if (tlModeIndex !== -1) {
    let modeDetails = formData.modes[tlModeIndex];
    let value = modeDetails["strategicQuantile"];
    if (
      value &&
      minValue &&
      maxValue &&
      (value < minValue || value > maxValue)
    ) {
      setValidationDataObj("tl", false, "", bidStrategy, "strategicQuantile");
    }
  }

  return validationData;
};

const hasValue = (value) => {
  if (Array.isArray(value)) {
    return value.length > 0;
  }
  return !!value;
};

function bidInformationValidation(formData, validationObj) {
  let validationData = cloneDeep(validationObj);
  let { bidInformation } = validationData;
  const specialCharacterRegex = /[^a-zA-Z0-9]/;

  formData.openDateUTC ||
    setValidationDataObj(
      "openDateUTC",
      false,
      messages.thisFieldIsRequired.defaultMessage,
      bidInformation
    );
  formData.closeDateUTC ||
    setValidationDataObj(
      "closeDateUTC",
      false,
      messages.thisFieldIsRequired.defaultMessage,
      bidInformation
    );
  formData.startDateUTC ||
    setValidationDataObj(
      "startDateUTC",
      false,
      messages.thisFieldIsRequired.defaultMessage,
      bidInformation
    );
  formData.endDateUTC ||
    setValidationDataObj(
      "endDateUTC",
      false,
      messages.thisFieldIsRequired.defaultMessage,
      bidInformation
    );
  formData.marginExpectationPercentage ||
    setValidationDataObj(
      "marginExpectationPercentage",
      false,
      messages.thisFieldIsRequired.defaultMessage,
      bidInformation
    );
  formData.rfqName ||
    setValidationDataObj(
      "rfqName",
      false,
      messages.thisFieldIsRequired.defaultMessage,
      bidInformation
    );
  formData.totalRounds ||
    setValidationDataObj(
      "totalRounds",
      false,
      messages.thisFieldIsRequired.defaultMessage,
      bidInformation
    );
  formData.bidType ||
    setValidationDataObj(
      "bidType",
      false,
      messages.thisFieldIsRequired.defaultMessage,
      bidInformation
    );

  formData.salesforceOpportunityId ||
    setValidationDataObj(
      "salesforceOpportunityId",
      false,
      messages.thisFieldIsRequired.defaultMessage,
      bidInformation
    );

  formData.salesforceOpportunityId &&
    formData.salesforceOpportunityId.length !== 18 &&
    setValidationDataObj(
      "salesforceOpportunityId",
      false,
      messages.invalidSalesforceIdLength.defaultMessage,
      bidInformation
    );

  formData.salesforceOpportunityId &&
    specialCharacterRegex.test(formData.salesforceOpportunityId) &&
    setValidationDataObj(
      "salesforceOpportunityId",
      false,
      messages.invalidSalesforceIdSpecialCharacters.defaultMessage,
      bidInformation
    );  

  if (formData.bidScac.value === bidScacType[0].value) {
    handleShipperValidation(formData, validationData);
    bidInformation.additionalBidRounds = [];
  } else {
    const totalRoundsValue =
      typeof formData.totalRounds === "object"
        ? formData.totalRounds?.value
        : parseInt(formData.totalRounds);

    const expectedAdditionalRounds = totalRoundsValue > 1 ? totalRoundsValue - 1 : 0;
    const additionalBidRounds = formData.additionalBidRounds || [];

    bidInformation.additionalBidRounds = [];
    const closeDateUTC = formData.closeDateUTC;
    const dateOnly = d => {
      const x = new Date(d);
      x.setHours(0, 0, 0, 0);
      return x.getTime();
    };
    for (let i = 0; i < expectedAdditionalRounds; i++) {
      const roundNum = i + 2;
      const round = additionalBidRounds.find(r => r.roundNumber === roundNum);
      const roundValidation = { isValid: true, validationMessage: "" };
      if (round?.dueDateUTC && closeDateUTC && dateOnly(round.dueDateUTC) < dateOnly(closeDateUTC)) {
        roundValidation.isValid = false;
        roundValidation.validationMessage = messages.roundCloseDateMustBeAfterRfqCloseDate.defaultMessage;
      }
      bidInformation.additionalBidRounds.push(roundValidation);
    }
    
    const locationCodeValue = formData.operationalCenters;
    
    hasValue(locationCodeValue) ||
      setValidationDataObj(
        "locationCode",
        false,
        messages.thisFieldIsRequired.defaultMessage,
        bidInformation
      );

    formData.previousSalesforceOpportunityId &&
      formData.previousSalesforceOpportunityId.length !== 18 &&
      setValidationDataObj(
        "previousSalesforceOpportunityId",
        false,
        messages.invalidSalesforceIdLength.defaultMessage,
        bidInformation
      );

    formData.previousSalesforceOpportunityId &&
      specialCharacterRegex.test(formData.previousSalesforceOpportunityId) &&
      setValidationDataObj(
        "previousSalesforceOpportunityId",
        false,
        messages.invalidSalesforceIdSpecialCharacters.defaultMessage,
        bidInformation
      );

    const foCodeValue = formData.freightOptimizerCodes;
    
    hasValue(foCodeValue) ||
      setValidationDataObj(
        "foShipperCode",
        false,
        messages.thisFieldIsRequired.defaultMessage,
        bidInformation
      );
  }

  const isImdlBid = formData.modes?.some(m => m.mode === mode.imdl);
  const isRxoNonImdlBid =
    formData.bidScac?.value === bidScacType[1].value && !isImdlBid;
  const dateOnly = d => {
    const x = new Date(d);
    x.setHours(0, 0, 0, 0);
    return x.getTime();
  };

  if (
    isRxoNonImdlBid &&
    formData.awardDateUTC &&
    formData.startDateUTC &&
    dateOnly(formData.awardDateUTC) > dateOnly(formData.startDateUTC)
  ) {
    setValidationDataObj(
      "awardDateUTC",
      false,
      messages.awardDateMustNotBeAfterRateEffectiveDate.defaultMessage,
      bidInformation
    );
  }

  const allValid = Object.entries(bidInformation).every(([key, item]) => {
    if (key === 'additionalBidRounds') {
      return Array.isArray(item) ? item.every(round => round.isValid === true) : true;
    }
    return item.isValid === true;
  });

  return { allValid, validationData };
}

function modeInformationValidation(formData, validationObj, division, crmEnableImdlUi = false) {
  let validationData = cloneDeep(validationObj);
  let { modeInformation } = validationData;

  const tlModeIndex = findIndex(formData.modes, m => m.mode === mode.tl);
  if (tlModeIndex !== -1) {
    let modeDetails = formData.modes[tlModeIndex];
    const tlFields = ["laneCount", "volume", "equipmentTypes", "currencyType"];

    if (
      division === divisionTypes.coy &&
      formData.bidScac.value !== bidScacType[0].value
    ) {
      tlFields.push(
        "customerBranchAndNetworkTimeframeStartDate",
        "customerBranchAndNetworkTimeframeEndDate"
      );
    }

    tlFields.forEach(
      field =>
        modeDetails[field] ||
        setValidationDataObj(
          "tl",
          false,
          messages.thisFieldIsRequired.defaultMessage,
          modeInformation,
          field
        )
    );

    if (formData.bidScac?.value === bidScacType[1].value) {
      const internationalLanes = modeDetails.internationalLanes;
      if (internationalLanes?.length) {
        const laneValues = internationalLanes.map(l => l.value);
        const hasCanada = laneValues.includes(1);
        const hasMexico = laneValues.includes(2);
        const hasBoth = laneValues.includes(3);

        if (
          internationalLanes.length > 1 ||
          (hasCanada && hasMexico && !hasBoth)
        ) {
          setValidationDataObj(
            "tl",
            false,
            messages.crossBorderInvalidSelection.defaultMessage,
            modeInformation,
            "internationalLanes"
          );
        }
      }
    }
  }

  const ltlModeIndex = findIndex(formData.modes, m => m.mode === mode.ltl);
  if (ltlModeIndex !== -1) {
    let modeDetails = formData.modes[ltlModeIndex];

    [
      "laneCount",
      "volume",
      "ltlFreightClasses",
      "palletsStackable",
      "ltlIncumbentCarriers",
      "ltlRateReturnFormat",
      "requiresGuaranteedShipment",
      "ltlAccessorials"
    ].forEach(
      field =>
        modeDetails[field] ||
        setValidationDataObj(
          "ltl",
          false,
          messages.thisFieldIsRequired.defaultMessage,
          modeInformation,
          field
        )
    );
  }

  const imdlModeIndex = findIndex(formData.modes, m => m.mode === mode.imdl);
  if (imdlModeIndex !== -1) {
    let modeDetails = formData.modes[imdlModeIndex];
    const imdlRequiredFields = ["laneCount", "volume", "equipmentLengths"];
    if (!crmEnableImdlUi) {
      imdlRequiredFields.push("fuelSurchargeType", "fuelSurchargePeg");
    } else {
      imdlRequiredFields.push("currencyType");
    }
    imdlRequiredFields.forEach(
      field =>
        modeDetails[field] ||
        setValidationDataObj(
          "imdl",
          false,
          messages.thisFieldIsRequired.defaultMessage,
          modeInformation,
          field
        )
    );
  }

  const allValid = Object.values(validationData.modeInformation).every(mode =>
    Object.values(mode).every(prop => prop.isValid === true)
  );

  return { allValid, validationData };
}

function bidStrategyValidation(formData, validationObj, crmEnableImdlUi = false) {
  let validationData = cloneDeep(validationObj);
  let { bidStrategy } = validationData;

  const tlModeIndex = findIndex(formData.modes, m => m.mode === mode.tl);
  if (tlModeIndex !== -1) {
    let modeDetails = formData.modes[tlModeIndex];
    setBidStrategyValidation(modeDetails, "tl", bidStrategy, formData.bidScac?.value === bidScacType[0].value, false);
  }

  const ltlModeIndex = findIndex(formData.modes, m => m.mode === mode.ltl);
  if (ltlModeIndex !== -1) {
    let modeDetails = formData.modes[ltlModeIndex];
    setBidStrategyValidation(modeDetails, "ltl", bidStrategy, formData.bidScac?.value === bidScacType[0].value, false);
  }

  const imdlModeIndex = findIndex(formData.modes, m => m.mode === mode.imdl);
  if (imdlModeIndex !== -1) {
    let modeDetails = formData.modes[imdlModeIndex];
    setBidStrategyValidation(modeDetails, "imdl", bidStrategy, formData.bidScac?.value === bidScacType[0].value, crmEnableImdlUi);
  }

  const allValid = Object.values(bidStrategy).every(mode =>
    Object.values(mode).every(prop => prop.isValid === true)
  );

  return { allValid, validationData };
}

const setBidStrategyValidation = (modeDetails, type, bidStrategy, isCoyoteBid, crmEnableImdlUi) => {
    if (crmEnableImdlUi && type === "imdl") {
        return;
    }
    if (isCoyoteBid) {
        [
            "bidStrategy",
            "shipperTenderStrategy",
            "expectedLanesPerDay",
            "goalLanesPerDay",
            "acceptanceParameters",
            "strategyNotes"
        ].forEach(
            field =>
                modeDetails[field] ||
                setValidationDataObj(
                    type,
                    false,
                    messages.thisFieldIsRequired.defaultMessage,
                    bidStrategy,
                    field
                )
        );
    }
};

function submitBidValidation(formData, validationData, division, crmEnableImdlUi = false) {
  const stepOneValidation = bidInformationValidation(formData, validationData);
  const stepTwoValidation = modeInformationValidation(
    formData,
    validationData,
    division,
    crmEnableImdlUi
  );
  const stepThreeValidation = bidStrategyValidation(formData, validationData, crmEnableImdlUi);

  return {
    validationData: {
      bidInformation: { ...stepOneValidation.validationData.bidInformation },
      modeInformation: { ...stepTwoValidation.validationData.modeInformation },
      bidStrategy: { ...stepThreeValidation.validationData.bidStrategy }
    },
    allValid:
      stepOneValidation.allValid &&
      stepTwoValidation.allValid &&
      stepThreeValidation.allValid
  };
}

export const removeNewBidValidation = (
  step,
  validationObj,
  field,
  childField
) => {
  let validationData = cloneDeep(validationObj);
  let { bidInformation, modeInformation, bidStrategy } = validationData;

  if (step === 1) {
    setValidationDataObj(field, true, "", bidInformation);
  } else if (step === 2) {
    setValidationDataObj(field, true, "", modeInformation, childField);
  } else if (step === 3) {
    setValidationDataObj(field, true, "", bidStrategy, childField);
  } else if (step === 4) {
    removeValidationForReviewStep(validationData, field, childField);
  }

  return { validationData };
};

function removeValidationForReviewStep(validationData, field, childField) {
  const bidInformationFields = [
    "openDateUTC",
    "closeDateUTC",
    "additionalBidRounds",
    "startDateUTC",
    "endDateUTC",
    "shipper",
    "bidOutputFormat",
    "mainSalesRep",
    "rfqName",
    "marginExpectationPercentage",
    "totalRounds",
    "salesforceOpportunityId",
    "locationCode",
    "foShipperCode",
    "awardDateUTC"
  ];

  const modeInformationFields = [
    "laneCount",
    "volume",
    "fuelSurchargeType",
    "fuelSurchargePeg",
    "equipmentTypes",
    "currencyType",
    "ltlFreightClasses",
    "palletsStackable",
    "ltlIncumbentCarriers",
    "ltlRateReturnFormat",
    "requiresGuaranteedShipment",
    "ltlAccessorials",
    "equipmentLengths",
    "rxoFuel",
    "customerFuel",
    "customerBranchAndNetworkTimeframeStartDate",
    "customerBranchAndNetworkTimeframeEndDate",
    "internationalLanes"
  ];

  const bidStrategyFields = [
    "bidStrategy",
    "shipperTenderStrategy",
    "expectedLanesPerDay",
    "goalLanesPerDay",
    "acceptanceParameters",
    "strategyNotes",
    "strategicQuantile"
  ];

  bidInformationFields.some(value => value === field) &&
    setValidationDataObj(field, true, "", validationData.bidInformation);
  modeInformationFields.some(value => value === childField) &&
    setValidationDataObj(
      field,
      true,
      "",
      validationData.modeInformation,
      childField
    );
  bidStrategyFields.some(value => value === childField) &&
    setValidationDataObj(
      field,
      true,
      "",
      validationData.bidStrategy,
      childField
    );
}

function setValidationDataObj(
  field,
  isValid,
  validationMessage,
  stepInformation,
  childField
) {
  if (childField) {
    stepInformation[field] = {
      ...stepInformation[field],
      [childField]: { isValid, validationMessage }
    };
  } else {
    stepInformation[field] = {
      isValid: isValid,
      validationMessage: validationMessage
    };
  }
}
